import {Injectable,} from '@angular/core';
import {AbstractControl, ValidationErrors, ValidatorFn, AsyncValidatorFn} from "@angular/forms";
import {UntilDestroy} from "@ngneat/until-destroy";
import {StoreService} from "./store.service";
import {Observable, of} from "rxjs";
import {debounceTime, switchMap, map, catchError} from "rxjs/operators";
import {ApiResponse} from '@app/core/api';


@UntilDestroy()
@Injectable({
  providedIn: 'root'
})


export class ValidatorsService {

  constructor(private storeService: StoreService) {
  }

  nonEmptyArray(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!Array.isArray(value) || value.length === 0) {
        return {required: true};
      }
      return null;
    };
  }

  coordinates(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value || !Array.isArray(value) || value.length === 0) {
        return { required: true };
      }

      const invalid = value.some(
        (coord) =>
          typeof coord.latitude !== 'number' ||
          typeof coord.longitude !== 'number'
      );

      return invalid ? { invalidCoordinate: true } : null;
    };
  }

  minCoordinates(min: number = 1): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || !Array.isArray(value)) return null;

      const actualLength = value.length;

      return actualLength < min
        ? {
          minCoordinates: {
            actualLength,
            requiredLength: min,
            valid: false
          }
        }
        : null;
    };
  }

  maxCoordinates(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || !Array.isArray(value)) return null;

      const actualLength = value.length;

      return actualLength > max
        ? {
          maxCoordinates: {
            actualLength,
            requiredLength: max,
            valid: false
          }
        }
        : null;
    };
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if(!control.value) return null;

      const isWhitespace = (control.value).trim().length === 0;
      return isWhitespace ? { whitespace: true } : null;
    };
  }

  checkMatchPassword(passwordControlName: any, newPasswordControlName: any): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const pass = currentControl.get(passwordControlName)?.value;
      const confirmPass = currentControl.get(newPasswordControlName)?.value;
      return pass === confirmPass ? null : {notSame: true};
    };

  }

  required(skipWhiteSpaceValidation?: any): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      let isValid;
      if (currentControl.value !== null && currentControl.value !== undefined) {
        if (currentControl.value === false || currentControl.value === true) {
          isValid = true;
        } else {
          if (skipWhiteSpaceValidation === true) {
            isValid = String(currentControl.value).length > 0;
          } else {
            isValid = String(currentControl.value).trim().length > 0;
          }
        }

      } else {
        isValid = false;
      }
      if (!isValid) {
        return {
          required: {
            valid: false
          }
        }
      } else {
        return null;
      }
    };
  }

  coefficient(coefficientParameter: any): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const value = parseInt(currentControl.value, 10);
      if (value % coefficientParameter !== 0) {
        return {
          coefficient: {
            coefficientParameter,
            valid: false
          }
        };
      } else {
        return null;
      }

    };
  }

  minLength(minLength: any, skipWhiteSpaceValidation?: any): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      let isValid;
      if (skipWhiteSpaceValidation === true) {
        isValid = String(currentControl.value).length >= minLength;
      } else {
        isValid = String(currentControl.value).trim().length >= minLength;
      }
      if (!isValid) {
        return {
          minlength: {
            actualLength: skipWhiteSpaceValidation ? String(currentControl.value).length : String(currentControl.value).trim().length,
            requiredLength: minLength,
            valid: false
          }
        };
      } else {
        return null;
      }
    };
  }

  maxLength(maxLength: any, skipWhiteSpaceValidation?: any): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      let isValid;
      if (skipWhiteSpaceValidation === true) {
        isValid = String(currentControl.value).length <= maxLength;
      } else {
        isValid = String(currentControl.value).trim().length <= maxLength;
      }
      if (!isValid) {
        return {
          maxLength: {
            actualLength: skipWhiteSpaceValidation ? String(currentControl.value).length : String(currentControl.value).trim().length,
            requiredLength: maxLength,
            valid: false
          }
        };
      } else {
        return null;
      }
    };
  }

  mail(): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const emailRegEx: RegExp = new RegExp('^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$');
      const isValid = emailRegEx.test(currentControl.value);
      if ((currentControl.value !== null && currentControl.value !== '') && !isValid) {
        return {
          incorrectMailFormat: {
            valid: false
          }
        };
      } else {
        return null;
      }
    }
  }

  mobile(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const regex = /^(09\d{9}|9\d{9})$/;
      const raw = control.value;

      if (!raw) {
        return null;
      }

      if(!regex.test(raw)) {
        return {
          incorrectMobile: {
            valid: false,
          }
        };
      }

      const prefixes = this.storeService.getPrefixes() || [];

      if (prefixes.length) {
        const prefix = parseInt(raw).toString().substring(0, 3);
        if (!prefixes.includes(prefix)) {
          return {
            incorrectMobile: {
              valid: false,
            }
          };
        }
      }
       return null
    };
  }

  nationalId(): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      let code = currentControl.value;
      const sameNumber = code === '0000000000'
        || code === '1111111111'
        || code === '2222222222'
        || code === '3333333333'
        || code === '4444444444'
        || code === '5555555555'
        || code === '6666666666'
        || code === '7777777777'
        || code === '8888888888'
        || code === '9999999999';
      const valueLength = code ? code.length : 0;
      if (valueLength === 0) {
        return null;
      }
      if (valueLength < 10 || parseInt(code, 10) === 0 || sameNumber) {
        return {
          nationalId: {
            valid: false,
          }
        };
      }
      code = ('0000' + code).substr(valueLength + 4 - 10);
      if (parseInt(code.substr(3, 6), 10) === 0) {
        return {
          nationalId: {
            valid: false,
          }
        };
      }
      const checkCode = parseInt(code.substr(9, 1), 10);
      let result = 0;
      for (let i = 0; i < 9; i++) {
        result += parseInt(code.substr(i, 1), 10) * (10 - i);
      }
      result = result % 11;
      if ((result < 2 && checkCode === result) || (result >= 2 && checkCode === (11 - result))) {
        return null;
      } else {
        return {
          nationalId: {
            valid: false,
          }
        };
      }
    }
  }

  certainLength(length: number): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const isValid = String(currentControl.value).length === length;
      if (!isValid) {
        return {
          certainLength: {
            length,
            valid: false
          }
        };
      } else {
        return null;
      }
    };
  }

  password(): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      // const passwordRegEx: RegExp = new RegExp('^(?=^.{8,}$)(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\\s)[0-9a-zA-Z!@#$%^&*()]*$');
      let isValid = false;
      if (currentControl.value.length >= 8 && currentControl.value.length <= 32) {
        isValid = true;
      }
      if (!isValid) {
        return {
          incorrectPasswordPattern: {
            valid: false
          }
        };
      } else {
        return null;
      }
    }
  }

  number({min, max}: any): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const value = currentControl.value;
      const price = parseInt(currentControl.value);
      if (isNaN(price)) {
        return {
          onlyNumbers: {
            valid: false
          }
        };
      } else {
        if (price > max) {
          return {
            inValidMax: {
              max,
              price,
              valid: false
            }
          };
        } else if (price < min) {
          return {
            inValidMin: {
              min,
              price,
              valid: false
            }
          };
        } else {
          return null;
        }
      }
    };
  }

  pattern(pattern: RegExp, message?: string): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const value = currentControl.value;
      if (!value) return null;
      const isValid = pattern.test(value);
      if (isValid) {
        return null;
      } else {
        return {
          pattern: {
            valid: false,
            message
          }
        };
      }
    };
  }

  telephone(): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const pattern: RegExp = /^(011|013|017|021|023|024|025|026|028|031|034|035|038|041|044|045|051|054|056|058|061|066|071|074|076|077|081|083|084|086|087)\d{4,8}/;
      const value = currentControl.value;
      const isValid = pattern.test(value);
      if (!isValid) {
        return {
          incorrectFormat: {
            text: "تلفن ثابت",
            valid: false
          }
        };
      } else {
        return null;
      }
    };
  }

  link(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (!value) return null;

      if (value.includes(' ') || !value.includes('.') || value.startsWith('.') || value.endsWith('.')) {
        return { link: { text: "فرمت لینک اشتباه است", valid: false } };
      }

      try {
        const urlToTest = /^https?:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(urlToTest);

        const hostname = url.hostname;
        const parts = hostname.split('.');

        const tld = parts.pop() || '';
        const isActuallyValid = parts.length >= 1 && tld.length >= 2;

        return isActuallyValid
          ? null
          : { link: { text: "آدرس سایت معتبر نیست", valid: false } };

      } catch (e) {
        return { link: { text: "فرمت لینک اشتباه است", valid: false } };
      }
    };
  }

  keywordValidation(): ValidatorFn {
    return (currentControl: AbstractControl): { [key: string]: any } | null => {
      const pattern = /^(?=.*[0-9])(?=.*[\u0600-\u06FFa-zA-Z])([\u0600-\u06FFa-zA-Z0-9]+)$/;
      const value = currentControl.value;
      const isValid = pattern.test(value);
      if (!isValid) {
        return {
          keywordValidation: {
            valid: false
          }
        };
      } else {
        if (value.includes('لغو') || value.includes('عضویت')) {
          return {
            keywordValidation: {
              valid: false
            }
          }
        } else {
          return null;
        }
      }
    };
  }

  templateParameter(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const pattern = /#([a-zA-Z0-9_-]+)#/g;
      const isValid = pattern.test(control.value);
      return isValid ? null : {templateParameterValidation: {valid: false}};
    };
  }

  digitsOnly(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const valid = /^\d+$/.test(control.value);
      return !valid && control.value ? {digitsOnly: true} : null;
    };
  }

  refCodeExistence(authService: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // This is a synchronous validator that will be used with async validator
      if (!control.value || control.value.trim().length === 0) {
        return null;
      }
      return null; // Let async validator handle the API call
    };
  }

  refCodeExistenceAsync(authService: any): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || control.value.trim().length === 0) {
        return of(null);
      }

      return of(control.value).pipe(
        debounceTime(5000),
        switchMap((value: any): Observable<ValidationErrors | null> => {
          if (!value || value.trim().length === 0) {
            return of(null);
          }

          return authService.existenceRefCode(value).pipe(
            map((response: ApiResponse<boolean>): ValidationErrors | null => {
              if (response.success && !response.result) {
                return { refCodeExists: { valid: false } };
              }
              return null;
            }),
            catchError((): Observable<ValidationErrors | null> => {
              return of({ refCodeExists: { valid: false, serverError: true } });
            })
          );
        })
      );
    };
  }

  numericRangeWithComma(min?: number, max?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = control.value;
      if (!rawValue) return null;

      const numericValue = Number(rawValue.toString().replace(/,/g, ''));

      if (isNaN(numericValue)) return { onlyNumbers: { valid: false } };

      if (min !== undefined && numericValue < min) return { min: { min, actual: numericValue } };

      if (max !== undefined && numericValue > max) return { max: { max, actual: numericValue } };

      return null;
    };
  }

  messengerBotDisplayName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '').trim();
      if (!value) {
        return null;
      }

      const hasPersian = /[\u0600-\u06FF]/.test(value);
      return hasPersian ? null : {messengerBotDisplayName: {valid: false}};
    };
  }
}
